package de.oevbs.kunde.kundenportal2.services.contract.lineofbusiness

import de.oevbs.businesspartner.bpcrmservice2.contract.model.lineofbusiness.Liability
import de.oevbs.businesspartner.bpcrmservice2.contract.model.lineofbusiness.LiabilityLegacy
import de.oevbs.businesspartner.bpcrmservice2.contract.model.lineofbusiness.liability.*
import de.oevbs.kunde.kundenportal2.components.*
import de.oevbs.kunde.kundenportal2.services.contract.*
import de.oevbs.sfa.data.time.toLocalizedString
import dev.fritz2.core.RenderContext


/**
 * Vertragsdetails der Sparte Haftpflicht
 *
 * Abhängig der [riskData] aus [liability] heraus wird das UI auch innerhalb dieser Sparte dediziert aufgebaut.
 *
 * @param liability beinhaltet die Sparten spezifischen Informationen
 */
fun RenderContext.contractDetailsLiability(liability: Liability) {
    detailsSectionBlank("Deklarationen - ${liability.product}") {

        liability.declarations.forEach { declaration ->
            collapsible {
                isRead(true)
                headerLeft {
                    div("flex flex-col gap-1 w-full") {
                        detailsGrid {
                            detailsGridEntry("Beschreibung", declaration.riskDescription)
                            when (declaration.riskData) {
                                is AnimalOwner -> {
                                    val riskData = declaration.riskData as AnimalOwner
                                    detailsGridEntry("Anzahl der Tiere", buildString {
                                        append(riskData.numberOfAnimals.toString())
                                        if (riskData.numberOfFightDogs > 0) {
                                            append(" (${riskData.numberOfFightDogs} Kampfhunde)")
                                        }
                                    })
                                }

                                is Hunt -> {
                                    val riskData = declaration.riskData as Hunt
                                    detailsGridEntry("Variante", riskData.variantText)
                                }

                                is BuildingOwner -> {
                                    val riskData = declaration.riskData as BuildingOwner
                                    detailsGridEntry("Gesamtbausumme", riskData.constructionSum.toEuro())
                                    detailsGridEntry("Davon Eigenleistung", riskData.contributionPersonal.toEuro())
                                    detailsGridEntry(
                                        "Einschluss Planung / Bauleitung",
                                        riskData.isConstructionManager.toJaNein()
                                    )
                                    detailsGridEntry("Versicherungsort", riskData.address.toView())
                                }

                                is HouseAndProperty -> {
                                    val riskData = declaration.riskData as HouseAndProperty
                                    detailsGridEntry("Versicherungsort", riskData.address.toView())
                                    detailsGridEntry("Tarifierung nach", riskData.tariffToName)
                                    detailsGridEntry("Private Nutzfläche", "${riskData.privateAreaQM} m²")
                                    detailsGridEntry("Gewerbliche Nutzfläche", "${riskData.commercialAreaQM} m²")
                                }

                                is SportBoat -> {
                                    val riskData = declaration.riskData as SportBoat
                                    if (riskData.motorStrength > 0) {
                                        detailsGridEntry("Motorstärke", "${riskData.motorStrength}kW")
                                        detailsGridEntry("Registrierungsdaten", riskData.registryData)
                                        detailsGridEntry("Hersteller", riskData.manufacturer)
                                        detailsGridEntry("Typ des Motors", riskData.motorType)
                                        detailsGridEntry("Motornummer", riskData.motorNumber)
                                    } else {
                                        detailsGridEntry("Segelfläche", "${riskData.sailArea}m²")
                                    }
                                }

                                is Water -> {
                                    val riskData = declaration.riskData as Water
                                    detailsGridEntry("Versicherungsort", riskData.address.toView())
                                    detailsGridEntry("Kapazität", "${riskData.capacity}m³")
                                    detailsGridEntry("Anzahl Behälter", "${riskData.numberOfTanks}")
                                    detailsGridEntry("Herstellungsdatum", riskData.productionDate?.toLocalizedString())
                                    detailsGridEntry("Einbaudatum", riskData.installationDate?.toLocalizedString())
                                    detailsGridEntry("Art der Tankwand", riskData.dripPanelSWText)
                                    detailsGridEntry("Schutzanstrich", riskData.protectiveCoatText)
                                    detailsGridEntry("Einfüllstutzen", riskData.hasFillingPlug.toJaNein())
                                    detailsGridEntry("Grenzwertgeber", riskData.hasLimitValueTransmitters.toJaNein())
                                }

                                else -> {}
                            }
                        }
                        detailsGrid {
                            detailsGridEntry("Versicherungssummen", {
                                detailsGridEntryValueList {
                                    declaration.coverage.forEach {
                                        li { +"${it.coverageName} ${it.insuredSum.toEuro()}" }
                                    }
                                }
                            }, longColumn = true)
                        }
                    }
                }
                content {
                    h5 { +"Klauseln" }
                    separator()
                    clausesSection(declaration.clauses)
                }
            }
        }
    }
}

/**
 * Vertragsdetails der Sparte Haftpflicht (ALTsystem)
 */
fun RenderContext.contractDetailsLiabilityLegacy(liability: LiabilityLegacy) {
    detailsSection("Ihr Versicherungsschutz") {
        detailsGrid {
            detailsGridEntry("Art der Versicherung", buildString {
                append(liability.lineOfBusinessText)
                if (liability.typeOfOperation.isNotEmpty()) {
                    append(" (${liability.typeOfOperation})")
                }
            })
            detailsGridEntry("Versicherungssummen", liability.insuranceSumText)
            detailsGridEntry(
                "Versicherungsort",
                "${liability.riskStreet} ${liability.riskHouseNumber}, ${liability.riskZipCode} ${liability.riskCity}",
                longColumn = true
            )
        }
    }

    if (liability.clauses.isNotEmpty()) {
        detailsSection("Klauseln") {
            liability.clauses.withLastElementFlag().forEach { (clause, isLastElement) ->
                detailsGrid {
                    detailsGridEntry("Bezeichnung", clause.clauseType)
                    detailsGridEntry("Artbezeichnung", clause.clauseIndividual)
                }
                if (!isLastElement) separator()
            }
        }
    }
}